/* eslint-disable @typescript-eslint/no-explicit-any */
import { removeTypenameFromMutationLink } from "apollo-remove-typename-mutation-link";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  gql as gqlCode,
  from,
  createHttpLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { split } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { enqueueSnackbar } from "notistack";
import { WebSocketLink } from "@apollo/client/link/ws";
import { authToken, containsAnyString } from "@/utility";
import { setContext } from "@apollo/client/link/context";
import { disconnect } from "@wagmi/core";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
class GraphqlClient {
  public client;
  private connected = false;

  constructor() {
    const errorCase = ["value too long for type character varying"];

    const httpLink = createHttpLink({
      uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
    });

    const authMiddleware = new ApolloLink((operation, forward) => {
      // add the authorization to the headers
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const accessToken = Cookies.get("x-access-token");
      let tokenIsValid = false;
      if (accessToken) {
        try {
          const decoded = jwtDecode(accessToken || "") as any;
          if (decoded.exp * 1000 < Date.now()) {
            Cookies.remove("x-access-token");
            tokenIsValid = false;
          } else {
            tokenIsValid = true;
          }
        } catch (err) {
          Cookies.remove("x-access-token");
        }
      }

      if (tokenIsValid) {
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${accessToken}`,
          },
        }));
      }

      return forward(operation).map((data) => {
        if (data.errors) {
          if (data.errors[0].extensions.code === "invalid-jwt") {
            localStorage.removeItem("user");

            enqueueSnackbar(
              "Your session has expired. Please log in again to continue.",
              { variant: "error" },
            );
            setTimeout(async () => {
              await disconnect();
              window.location.href = "/";
            }, 2000);

            throw {
              code: "token expired",
              message:
                "Your session has expired. Please log in again to continue.",
            };
          } else {
            console.warn(`[GraphQL error]: `, data.errors);

            if (!containsAnyString(errorCase, data.errors[0].message)) {
              if (data.errors[0].message.includes("Uniqueness violation.")) {
                throw {
                  message: data.errors[0].message.substring(22),
                };
              } else {
                throw {
                  message: "Something went wrong, please try again later.",
                };
              }
            }
          }
        }
        return data;
      });
    });

    const link = from([
      removeTypenameFromMutationLink,
      authMiddleware,
      httpLink,
    ]);

    const wsLink = this.wsLinkFactory();

    const errorLink = onError(({ graphQLErrors }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ extensions }) => {
          if (extensions && extensions.code === "invalid-jwt") {
            localStorage.removeItem("user");

            enqueueSnackbar(
              "Your session has expired. Please log in again to continue.",
              { variant: "error" },
            );
            setTimeout(() => {
              window.location.href = "/";
            }, 2000);
          } else {
            console.warn(`[GraphQL error]: `, extensions.errors);

            throw {
              code: "gql",
              message: "Something went wrong, please try again later.",
            };
          }
        });
      }
    });

    const combinedLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      ApolloLink.from([errorLink, wsLink]),
      link,
    );

    this.client = new ApolloClient({
      link: combinedLink,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
          errorPolicy: "ignore",
        },
        query: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
      // when request is sent to server, it will be intercepted by this link and the token will be added to the header
    });
  }
  public wsLinkFactory = () => {
    const token = authToken().authorization;
    const wsLink = new WebSocketLink({
      uri: process.env.REACT_APP_GRAPHQL_ENDPOINT_WS || "",
      options: {
        reconnect: true,
        connectionParams: {
          headers: { ...(token ? { authorization: token } : {}) },
        },
        connectionCallback: (error) => {
          if (!error) {
            this.connected = true;
          }
        },
      },
    });
    return setContext((_, { headers }) => {
      const token = authToken().authorization;
      return {
        headers: {
          ...headers,
          ...(token ? { authorization: token } : {}),
        },
      };
    }).concat(wsLink);
  };
  public updateWsLink() {
    this.client.setLink(this.wsLinkFactory());
  }
  public Query = async (query: string, variables: any = {}) =>
    await this.client.query({
      query: gqlCode`
        ${query}
      `,
      variables,
    });

  public Mutation = async (query: string, variables: any = {}) =>
    await this.client.mutate(this.CreateMutation(variables, query));

  public CreateQuery = (variables: any, query: string) => {
    return {
      query: gqlCode`
        ${query}
      `,
      variables,
    };
  };
  public CreateMutation = (variables: any, query: string) => {
    return {
      mutation: gqlCode`
        ${query}
      `,
      variables,
    };
  };
  public Subscribe = async (query: string, variables: any = {}) => {
    while (!this.connected) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    return this.client.subscribe({
      query: gqlCode`
        ${query}
      `,
      variables,
    });
  };
}

const gql = new GraphqlClient();
export default gql;
